import React from 'react';

import {
  Alert,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import Spinner from '~/components/Common/Spinner';

type Row = Record<string, unknown>;

type Props = {
  title: string;
  description: string;
  rows: Array<Row>;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  emptyMessage: string;
  columns: Array<{ key: string; label: string }>;
};

function firstPresent(row: Row, keys: Array<string>) {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== '') {
      return String(value);
    }
  }
  return '—';
}

function rowLabel(row: Row, fallbackKey: string) {
  return firstPresent(row, [fallbackKey, 'name', 'title', 'label', 'audience_name', 'audienceId', 'audience_id', 'id']);
}

export default function AudiencesListTable({
  title,
  description,
  rows,
  isLoading,
  isError,
  errorMessage,
  emptyMessage,
  columns,
}: Props) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Stack>

      {isLoading ? <Spinner /> : null}
      {isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      ) : null}
      {!isLoading && !isError && rows.length === 0 ? <Alert severity="info">{emptyMessage}</Alert> : null}
      {!isLoading && !isError && rows.length > 0 ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={180}>Label</TableCell>
                {columns.map((column) => (
                  <TableCell key={column.key}>{column.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={String(row.id ?? row.audience_id ?? row.audienceUserId ?? index)} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{rowLabel(row, 'id')}</TableCell>
                  {columns.map((column) => (
                    <TableCell key={column.key}>{firstPresent(row, [column.key])}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </Paper>
  );
}
